import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import {
  CollapseSection,
  LabeledValue,
  LabeledValueWithButton,
  MenuButton,
  Modal,
  ModalOpenButton,
  Select,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import { Grid, IconButton, MenuItem, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { differenceWith, noop, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  type DocType,
  EntitySearchArrayOfFilterOperator,
  EntitySearchBoolFilterOperator,
  EntitySearchBoolInFilterOperator,
  EntitySearchNumberBetweenFilterOperator,
  EntitySearchNumberFilterOperator,
  EntitySearchNumberInFilterOperator,
  EntitySearchTextFilterOperator,
  EntitySearchTextInFilterOperator,
  ItemType,
  ModifyItemRecommendedTemplateTypeValuesInput,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import type { DocumentByType } from "../../auth/RestrictedByDocumentPermissionByType.js";
import { FullScreenToggleIconButton } from "../../commons/button/FullScreenToggleIconButton.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { ViewMode, ViewModeMenuItem } from "../../commons/ViewModeMenuItem.js";
import type { TemplateSearchSortingDefinitionFragment } from "../doc-items/boxes/TemplateTypePlaceholderBox.generated.js";
import { getValueWithOperator } from "../doc-items/boxes/TemplateTypePlaceholderBox.js";
import {
  ExpressionInputWithVariables,
  type VariableInputState,
  type ExpressionItemSource,
} from "../doc-items/modals/ExpressionInputWithVariables.js";
import { ProductSearchFilterExpressionModal } from "../doc-items/modals/ProductSearchFilterExpressionModal.js";
import { useExpressionValidation } from "../doc-items/modals/useExpressionValidation.js";
import {
  getEnhancedPropertyLabel,
  getEnhancedTemplateSearchFilterPropLabel,
  propToTemplateSearchFilterProp,
} from "../doc-items/properties.js";
import { templateSearchFilterPropertyFilterExpression2Input } from "../doc-items/useTemplateSearchFiltersMutations.js";
import { templateSearchSortingPropertySortingDefinition2Input } from "../doc-items/useTemplateSearchSortingsMutations.js";
import {
  ArrayFilterOperatorOptions,
  BoolFilterOperatorOptions,
  NumberFilterOperatorOptions,
  TextFilterOperatorOptions,
} from "../products/ProductSearchFilterOperatorOptions.js";
import {
  EditRecommendedTemplateTypeModal_ItemRecommendationConfigFragment,
  EditRecommendedTemplateTypeModal_ItemRecommendationFragment,
  EditRecommendedTemplateTypeModal_RecommendedTemplateTypeConfigFragment,
  useEditRecommendedTemplateTypeModalQuery,
} from "./EditRecommendedTemplateTypeModal.generated.js";
import type { ItemTemplateSearchFilterPropertyFilterComputedFragment } from "./templateSearchFilters.generated.js";

const EXPRESSION_SOURCES: ExpressionItemSource[] = ["self"];

interface FormValues {
  includeIfExpr: VariableInputState;
  eligibleIfExpr: VariableInputState;
  templateSearchFilterExpressions: {
    propertyFilters: ItemTemplateSearchFilterPropertyFilterComputedFragment[];
  };
  templateSearchSortingDefinitions: TemplateSearchSortingDefinitionFragment[];
}

interface Props {
  docId: string;
  docType: DocType;
  itemId: string;
  templateTypeId: string;
  recommendation: EditRecommendedTemplateTypeModal_ItemRecommendationFragment;
  recommendationConfig: EditRecommendedTemplateTypeModal_ItemRecommendationConfigFragment;
  recommendedTemplateTypeConfig: EditRecommendedTemplateTypeModal_RecommendedTemplateTypeConfigFragment;
  allowedItemTypes: ItemType[];
  handleClose(): void;
  handleComplete(
    values: ModifyItemRecommendedTemplateTypeValuesInput | null
  ): Promise<void> | void;
  documentViewerPermissions: PermissionName[];
}

export const EditRecommendedTemplateTypeModal = ({
  docId,
  itemId,
  templateTypeId,
  recommendation,
  recommendationConfig,
  recommendedTemplateTypeConfig,
  allowedItemTypes,
  handleClose,
  handleComplete,
  docType,
  documentViewerPermissions,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "TemplateTypes", "Global"]);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const { isMaxPhone } = useScreenWidth();
  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-recommended-template-type-modal-fullscreen",
    false,
    true
  );

  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const query = useEditRecommendedTemplateTypeModalQuery({
    client,
    variables: { templateTypeId },
  });
  const templateType = getDataOrNull(query.data?.templateType);

  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

  const [viewModePropertyExpression, setViewModePropertyExpression] =
    React.useState<ViewMode>(null);
  const [viewModeSearchSortings, setViewModeSearchSortings] =
    React.useState<ViewMode>(null);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const { expressionCheckResults, validateExpressions } =
    useExpressionValidation(docId, itemId);
  const eligibleIfExprError = expressionCheckResults?.find(
    value =>
      value.__typename ===
        "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf" &&
      value.itemRecommendationId === recommendation.id
  );
  const includeIfExprError = expressionCheckResults?.find(
    value =>
      value.__typename ===
        "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf" &&
      value.itemRecommendationId === recommendation.id
  );

  const initialValues: FormValues = {
    includeIfExpr: {
      expression: recommendationConfig.includeIfExpr,
      cursorIndex: null,
    },
    eligibleIfExpr: {
      expression: recommendationConfig.eligibleIfExpr,
      cursorIndex: null,
    },
    templateSearchFilterExpressions:
      recommendedTemplateTypeConfig.templateSearchFilterExpressions,
    templateSearchSortingDefinitions:
      recommendedTemplateTypeConfig.templateSearchSortingDefinitions,
  };

  const formId = React.useMemo(() => uniqueId(), []);

  const validationSchema = Yup.object().shape({
    includeIfExpr: Yup.object().required().shape({
      expression: Yup.string(),
      cursorIndex: Yup.number().nullable(),
    }),
    eligibleIfExpr: Yup.object().required().shape({
      expression: Yup.string(),
      cursorIndex: Yup.number().nullable(),
    }),
    templateSearchFilterExpressions: Yup.mixed().required(),
  });

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        const result = await validateExpressions({
          docId,
          itemId,
          overrides: [
            {
              itemId,
              recommended: {
                eligibleIf: values.eligibleIfExpr.expression,
                includeIf: values.includeIfExpr.expression,
                recommendationId: recommendation.id,
              },
            },
          ],
        });

        const expressionCheckResult = getDataOrNull(
          result.data?.compileDocIsolatedExpression
        )?.results?.filter(
          result =>
            (result.__typename ===
              "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf" ||
              result.__typename ===
                "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf") &&
            result.itemRecommendationId === recommendation.id
        );

        if (!expressionCheckResult) {
          console.error("compile document error");
          enqueueSnackbar(`Cannot compile expressions! Not allowed`, {
            variant: "error",
          });
          return;
        }
        if (expressionCheckResult.length > 0) {
          console.error("compile document error", expressionCheckResult);
          expressionCheckResult.forEach(result => {
            enqueueSnackbar(`Expression is invalid: ${result.messageText}`, {
              variant: "error",
            });
          });
          return;
        }

        await handleComplete({
          includeIfExpr: values.includeIfExpr.expression,
          eligibleIfExpr: values.eligibleIfExpr.expression,
          templateSearchFilterExpressions: {
            propertyFilterExpressions:
              values.templateSearchFilterExpressions.propertyFilters.map(
                templateSearchFilterPropertyFilterExpression2Input
              ),
          },
          templateSearchSortingDefinitions:
            values.templateSearchSortingDefinitions.map(
              templateSearchSortingPropertySortingDefinition2Input
            ),
        });
        handleClose();
      }}
    >
      {formikProps => {
        const propertySearchFiltersExpressions =
          formikProps.values.templateSearchFilterExpressions?.propertyFilters;

        const searchSortingDefinitions =
          formikProps.values.templateSearchSortingDefinitions;

        const unusedFilterProperties = differenceWith(
          templateType?.props2 ?? [],
          propertySearchFiltersExpressions,
          (p, f) => p.key === f.key
        );

        const unusedSortingProperties = differenceWith(
          templateType?.props2 ?? [],
          searchSortingDefinitions.filter(
            d => d.__typename === "EntitySearchSortingPropertyValueSorting"
          ),
          (p, f) => p.key === f.key
        ).flatMap(p => (p.__typename === "Props2Number" ? p : []));

        return (
          <Modal
            maxWidth="md"
            title={t("Configure template type: {templateName}", {
              ns: "QuoteItem",
              templateName: recommendation.teaserTitle,
            })}
            handleClose={handleClose}
            dialogProps={
              !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
            }
            headerActions={
              !isMaxPhone ? (
                <FullScreenToggleIconButton
                  isFullScreen={isFullScreen}
                  setIsFullScreen={setIsFullScreen}
                />
              ) : undefined
            }
            alwaysVisible
            actionButtons={[
              {
                label: t("Cancel", { ns: "Global" }),
                handleClick: handleClose,
                buttonProps: {
                  variant: "text",
                  disabled: formikProps.isSubmitting,
                },
              },
              {
                label: t("Remove template type", { ns: "QuoteItem" }),
                handleClick: async () => {
                  if (isDeleting) return;
                  const result = await startConfirmProcess({
                    title: t("Confirm deletion", { ns: "Global" }),
                    text: t(
                      "Are you sure you would like to delete this recommended template type?",
                      {
                        ns: "QuoteItem",
                      }
                    ),
                    confirmButtonLabel: t("Delete", { ns: "Global" }),
                  });
                  if (!result) return;
                  setIsDeleting(true);
                  try {
                    await handleComplete(null);
                  } finally {
                    setIsDeleting(false);
                  }
                  handleClose();
                },
                buttonProps: {
                  variant: "outlined",
                  disabled: formikProps.isSubmitting || isDeleting,
                },
              },
              {
                label: t("Save", { ns: "Global" }),
                buttonProps: {
                  form: formId,
                  type: "submit",
                  loading: formikProps.isSubmitting,
                  disabled: !formikProps.isValid || !formikProps.dirty,
                },
              },
            ]}
          >
            <Form id={formId}>
              <Stack direction="column" spacing={2}>
                <CollapseSection
                  title={t("Rules", { ns: "QuoteItem" })}
                  isInitiallyExpanded
                >
                  <Stack direction="column" spacing={1}>
                    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                      capability="EXPRESSIONS"
                      permissionByType={{
                        QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                        REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                        TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                        BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                      }}
                      document={document}
                      otherwise={
                        <ExpressionInputWithVariables
                          label={t("Eligible if", { ns: "QuoteItem" })}
                          placeholder={t("Formula", { ns: "QuoteItem" })}
                          itemId={itemId}
                          docId={docId}
                          projectId={null}
                          inputState={formikProps.values.eligibleIfExpr}
                          disabled={true}
                          direction="row"
                          onInputStateChange={noop}
                        />
                      }
                    >
                      <ExpressionInputWithVariables
                        label={t("Eligible if", { ns: "QuoteItem" })}
                        placeholder={t("Formula", { ns: "QuoteItem" })}
                        itemId={itemId}
                        docId={docId}
                        projectId={null}
                        expressionError={
                          eligibleIfExprError
                            ? new Error(eligibleIfExprError.messageText)
                            : null
                        }
                        inputState={formikProps.values.eligibleIfExpr}
                        onInputStateChange={expr => {
                          formikProps.setFieldValue("eligibleIfExpr", expr);
                        }}
                        autoFocus={false}
                        direction="row"
                        allowedSources={EXPRESSION_SOURCES}
                        showSelectedVariableAllowedValues={true}
                      />
                    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                      capability="EXPRESSIONS"
                      permissionByType={{
                        QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                        REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                        TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                        BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                      }}
                      document={document}
                      otherwise={
                        <ExpressionInputWithVariables
                          label={t("Include if", { ns: "QuoteItem" })}
                          placeholder={t("Formula", { ns: "QuoteItem" })}
                          itemId={itemId}
                          docId={docId}
                          projectId={null}
                          inputState={formikProps.values.includeIfExpr}
                          disabled={true}
                          direction="row"
                          onInputStateChange={noop}
                        />
                      }
                    >
                      <ExpressionInputWithVariables
                        label={t("Include if", { ns: "QuoteItem" })}
                        placeholder={t("Formula", { ns: "QuoteItem" })}
                        itemId={itemId}
                        docId={docId}
                        projectId={null}
                        expressionError={
                          includeIfExprError
                            ? new Error(includeIfExprError.messageText)
                            : null
                        }
                        inputState={formikProps.values.includeIfExpr}
                        onInputStateChange={expr => {
                          formikProps.setFieldValue("includeIfExpr", expr);
                        }}
                        autoFocus={false}
                        direction="row"
                        allowedSources={EXPRESSION_SOURCES}
                        showSelectedVariableAllowedValues={true}
                      />
                    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                  </Stack>
                </CollapseSection>
                <CollapseSection
                  title={t("Expressions", { ns: "TemplateTypes" })}
                  ActionButtons={
                    <>
                      <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                        capability="EXPRESSIONS"
                        permissionByType={{
                          QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                          REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                          TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                          BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                        }}
                        document={document}
                      >
                        <MenuButton
                          Icon={<AddIcon />}
                          buttonProps={{
                            disabled: unusedFilterProperties.length === 0,
                          }}
                        >
                          {unusedFilterProperties.map(prop => (
                            <MenuItem
                              key={prop.key}
                              onClick={() =>
                                formikProps.setValues(values => ({
                                  ...values,
                                  templateSearchFilterExpressions: {
                                    propertyFilters:
                                      values.templateSearchFilterExpressions.propertyFilters.concat(
                                        propToTemplateSearchFilterProp(prop)
                                      ),
                                  },
                                }))
                              }
                            >
                              {getEnhancedPropertyLabel(prop, false)}
                            </MenuItem>
                          ))}
                        </MenuButton>
                      </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                      <MenuButton>
                        <ViewModeMenuItem
                          allowedModes={[null, "delete"]}
                          viewMode={viewModePropertyExpression}
                          onViewModeChange={setViewModePropertyExpression}
                        />
                      </MenuButton>
                    </>
                  }
                >
                  <Grid container columns={2} rowSpacing={1} columnSpacing={2}>
                    {propertySearchFiltersExpressions.map(
                      (propertySearchFilter, index) => {
                        switch (propertySearchFilter.__typename) {
                          case "EntitySearchPropertyFilterBoolFilterComputed":
                          case "EntitySearchPropertyFilterBoolInFilterComputed": {
                            return (
                              <Grid key={propertySearchFilter.key} size={1}>
                                <Stack
                                  direction={"row"}
                                  spacing={0.5}
                                  alignItems={"center"}
                                  justifyContent={"space-between"}
                                >
                                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                                    capability="EXPRESSIONS"
                                    permissionByType={{
                                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                                      REQUIREMENT:
                                        "MANAGE_REQUIREMENTS_EXPRESSIONS",
                                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                                    }}
                                    document={document}
                                    otherwise={
                                      <LabeledValue
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValue>
                                    }
                                  >
                                    <ModalOpenButton
                                      Modal={
                                        ProductSearchFilterExpressionModal<
                                          | EntitySearchBoolFilterOperator
                                          | EntitySearchBoolInFilterOperator
                                        >
                                      }
                                      modalProps={{
                                        projectId: null,
                                        docId: docId,
                                        itemId: itemId,
                                        filterLabel:
                                          getEnhancedTemplateSearchFilterPropLabel(
                                            propertySearchFilter
                                          ),
                                        operator:
                                          propertySearchFilter.__typename ===
                                          "EntitySearchPropertyFilterBoolInFilterComputed"
                                            ? propertySearchFilter.operatorBoolIn
                                            : propertySearchFilter.operatorBool,
                                        allowedSearchFilterOperators:
                                          BoolFilterOperatorOptions,
                                        expression: propertySearchFilter.expr,
                                        handleComplete: (
                                          value,
                                          handleClose
                                        ) => {
                                          formikProps.setValues(values => ({
                                            ...values,
                                            templateSearchFilterExpressions: {
                                              propertyFilters:
                                                values.templateSearchFilterExpressions.propertyFilters
                                                  .filter(
                                                    v =>
                                                      v.key !==
                                                      propertySearchFilter.key
                                                  )
                                                  .concat(
                                                    value.operator === "in"
                                                      ? {
                                                          __typename:
                                                            "EntitySearchPropertyFilterBoolInFilterComputed",
                                                          key: propertySearchFilter.key,
                                                          operatorBoolIn:
                                                            value.operator,
                                                          expr: value.expr,
                                                          missingValue: true,
                                                          valueBoolInComputed:
                                                            null,
                                                        }
                                                      : value.operator === "eq"
                                                        ? {
                                                            __typename:
                                                              "EntitySearchPropertyFilterBoolFilterComputed",
                                                            operatorBool:
                                                              value.operator,
                                                            expr: value.expr,
                                                            key: propertySearchFilter.key,
                                                            missingValue: true,
                                                            valueBoolComputed:
                                                              null,
                                                          }
                                                        : assertNever(
                                                            value.operator
                                                          )
                                                  ),
                                            },
                                          }));
                                          handleClose();
                                        },
                                      }}
                                      disabled={false}
                                    >
                                      <LabeledValueWithButton
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValueWithButton>
                                    </ModalOpenButton>
                                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                                  {viewModePropertyExpression === "delete" && (
                                    <IconButton
                                      onClick={() => {
                                        formikProps.setValues(values => ({
                                          ...values,
                                          templateSearchFilterExpressions: {
                                            propertyFilters:
                                              values.templateSearchFilterExpressions.propertyFilters.filter(
                                                v =>
                                                  v.key !==
                                                  propertySearchFilter.key
                                              ),
                                          },
                                        }));
                                      }}
                                      color="primary"
                                      size="small"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Grid>
                            );
                          }
                          case "EntitySearchPropertyFilterNumberFilterComputed":
                          case "EntitySearchPropertyFilterNumberInFilterComputed":
                          case "EntitySearchPropertyFilterNumberBetweenFilterComputed": {
                            return (
                              <Grid key={propertySearchFilter.key} size={1}>
                                <Stack
                                  direction={"row"}
                                  spacing={0.5}
                                  alignItems={"center"}
                                  justifyContent={"space-between"}
                                >
                                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                                    capability="EXPRESSIONS"
                                    permissionByType={{
                                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                                      REQUIREMENT:
                                        "MANAGE_REQUIREMENTS_EXPRESSIONS",
                                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                                    }}
                                    document={document}
                                    otherwise={
                                      <LabeledValue
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValue>
                                    }
                                  >
                                    <ModalOpenButton
                                      Modal={
                                        ProductSearchFilterExpressionModal<
                                          | EntitySearchNumberFilterOperator
                                          | EntitySearchNumberInFilterOperator
                                          | EntitySearchNumberBetweenFilterOperator
                                        >
                                      }
                                      modalProps={{
                                        projectId: null,
                                        docId: docId,
                                        itemId: itemId,
                                        filterLabel:
                                          getEnhancedTemplateSearchFilterPropLabel(
                                            propertySearchFilter
                                          ),
                                        operator:
                                          propertySearchFilter.__typename ===
                                          "EntitySearchPropertyFilterNumberFilterComputed"
                                            ? propertySearchFilter.operatorNumber
                                            : propertySearchFilter.__typename ===
                                                "EntitySearchPropertyFilterNumberInFilterComputed"
                                              ? propertySearchFilter.operatorNumberIn
                                              : propertySearchFilter.operatorNumberBetween,
                                        allowedSearchFilterOperators:
                                          NumberFilterOperatorOptions,
                                        expression: propertySearchFilter.expr,
                                        handleComplete: (
                                          value,
                                          handleClose
                                        ) => {
                                          formikProps.setValues(values => ({
                                            ...values,
                                            templateSearchFilterExpressions: {
                                              propertyFilters:
                                                values.templateSearchFilterExpressions.propertyFilters
                                                  .filter(
                                                    v =>
                                                      v.key !==
                                                      propertySearchFilter.key
                                                  )
                                                  .concat(
                                                    value.operator === "in"
                                                      ? {
                                                          __typename:
                                                            "EntitySearchPropertyFilterNumberInFilterComputed",
                                                          key: propertySearchFilter.key,
                                                          operatorNumberIn:
                                                            value.operator,
                                                          expr: value.expr,
                                                          missingValue: true,
                                                          valueNumberInComputed:
                                                            null,
                                                        }
                                                      : value.operator ===
                                                          "between"
                                                        ? {
                                                            __typename:
                                                              "EntitySearchPropertyFilterNumberBetweenFilterComputed",
                                                            key: propertySearchFilter.key,
                                                            operatorNumberBetween:
                                                              value.operator,
                                                            expr: value.expr,
                                                            missingValue: true,
                                                            valueNumberBetweenComputed:
                                                              null,
                                                          }
                                                        : value.operator ===
                                                              "eq" ||
                                                            value.operator ===
                                                              "lt" ||
                                                            value.operator ===
                                                              "lte" ||
                                                            value.operator ===
                                                              "gt" ||
                                                            value.operator ===
                                                              "gte"
                                                          ? {
                                                              __typename:
                                                                "EntitySearchPropertyFilterNumberFilterComputed",
                                                              key: propertySearchFilter.key,
                                                              operatorNumber:
                                                                value.operator,
                                                              expr: value.expr,
                                                              missingValue: true,
                                                              valueNumberComputed:
                                                                null,
                                                            }
                                                          : assertNever(
                                                              value.operator
                                                            )
                                                  ),
                                            },
                                          }));

                                          handleClose();
                                        },
                                      }}
                                      disabled={false}
                                    >
                                      <LabeledValueWithButton
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValueWithButton>
                                    </ModalOpenButton>
                                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                                  {viewModePropertyExpression === "delete" && (
                                    <IconButton
                                      onClick={() => {
                                        formikProps.setValues(values => ({
                                          ...values,
                                          templateSearchFilterExpressions: {
                                            propertyFilters:
                                              values.templateSearchFilterExpressions.propertyFilters.filter(
                                                v =>
                                                  v.key !==
                                                  propertySearchFilter.key
                                              ),
                                          },
                                        }));
                                      }}
                                      color="primary"
                                      size="small"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Grid>
                            );
                          }
                          case "EntitySearchPropertyFilterTextFilterComputed":
                          case "EntitySearchPropertyFilterTextInFilterComputed": {
                            return (
                              <Grid key={propertySearchFilter.key} size={1}>
                                <Stack
                                  direction={"row"}
                                  spacing={0.5}
                                  alignItems={"center"}
                                  justifyContent={"space-between"}
                                >
                                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                                    capability="EXPRESSIONS"
                                    permissionByType={{
                                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                                      REQUIREMENT:
                                        "MANAGE_REQUIREMENTS_EXPRESSIONS",
                                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                                    }}
                                    document={document}
                                    otherwise={
                                      <LabeledValue
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValue>
                                    }
                                  >
                                    <ModalOpenButton
                                      Modal={
                                        ProductSearchFilterExpressionModal<
                                          | EntitySearchTextFilterOperator
                                          | EntitySearchTextInFilterOperator
                                        >
                                      }
                                      modalProps={{
                                        projectId: null,
                                        docId: docId,
                                        itemId: itemId,
                                        filterLabel:
                                          getEnhancedTemplateSearchFilterPropLabel(
                                            propertySearchFilter
                                          ),
                                        operator:
                                          propertySearchFilter.__typename ===
                                          "EntitySearchPropertyFilterTextFilterComputed"
                                            ? propertySearchFilter.operatorText
                                            : propertySearchFilter.operatorTextIn,
                                        allowedSearchFilterOperators:
                                          TextFilterOperatorOptions,
                                        expression: propertySearchFilter.expr,
                                        handleComplete: (
                                          value,
                                          handleClose
                                        ) => {
                                          formikProps.setValues(values => ({
                                            ...values,
                                            templateSearchFilterExpressions: {
                                              propertyFilters:
                                                values.templateSearchFilterExpressions.propertyFilters
                                                  .filter(
                                                    v =>
                                                      v.key !==
                                                      propertySearchFilter.key
                                                  )
                                                  .concat(
                                                    value.operator === "in"
                                                      ? {
                                                          __typename:
                                                            "EntitySearchPropertyFilterTextInFilterComputed",
                                                          key: propertySearchFilter.key,
                                                          operatorTextIn:
                                                            value.operator,
                                                          expr: value.expr,
                                                          missingValue: true,
                                                          valueTextInComputed:
                                                            null,
                                                        }
                                                      : value.operator === "eq"
                                                        ? {
                                                            __typename:
                                                              "EntitySearchPropertyFilterTextFilterComputed",
                                                            key: propertySearchFilter.key,
                                                            operatorText:
                                                              value.operator,
                                                            expr: value.expr,
                                                            missingValue: true,
                                                            valueTextComputed:
                                                              null,
                                                          }
                                                        : assertNever(
                                                            value.operator
                                                          )
                                                  ),
                                            },
                                          }));

                                          handleClose();
                                        },
                                      }}
                                      disabled={false}
                                    >
                                      <LabeledValueWithButton
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValueWithButton>
                                    </ModalOpenButton>
                                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                                  {viewModePropertyExpression === "delete" && (
                                    <IconButton
                                      onClick={() => {
                                        formikProps.setValues(values => ({
                                          ...values,
                                          templateSearchFilterExpressions: {
                                            propertyFilters:
                                              values.templateSearchFilterExpressions.propertyFilters.filter(
                                                v =>
                                                  v.key !==
                                                  propertySearchFilter.key
                                              ),
                                          },
                                        }));
                                      }}
                                      color="primary"
                                      size="small"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Grid>
                            );
                          }
                          case "EntitySearchPropertyFilterNumberArrayOfFilterComputed": {
                            return (
                              <Grid key={propertySearchFilter.key} size={1}>
                                <Stack
                                  direction={"row"}
                                  spacing={0.5}
                                  alignItems={"center"}
                                  justifyContent={"space-between"}
                                >
                                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                                    capability="EXPRESSIONS"
                                    permissionByType={{
                                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                                      REQUIREMENT:
                                        "MANAGE_REQUIREMENTS_EXPRESSIONS",
                                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                                    }}
                                    document={document}
                                    otherwise={
                                      <LabeledValue
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValue>
                                    }
                                  >
                                    <ModalOpenButton
                                      Modal={
                                        ProductSearchFilterExpressionModal<EntitySearchArrayOfFilterOperator>
                                      }
                                      modalProps={{
                                        projectId: null,
                                        docId: docId,
                                        itemId: itemId,
                                        filterLabel:
                                          getEnhancedTemplateSearchFilterPropLabel(
                                            propertySearchFilter
                                          ),
                                        operator:
                                          propertySearchFilter.operatorNumberArrayOf,
                                        allowedSearchFilterOperators:
                                          ArrayFilterOperatorOptions,
                                        expression: propertySearchFilter.expr,
                                        handleComplete: (
                                          value,
                                          handleClose
                                        ) => {
                                          formikProps.setValues(values => ({
                                            ...values,
                                            templateSearchFilterExpressions: {
                                              propertyFilters:
                                                values.templateSearchFilterExpressions.propertyFilters
                                                  .filter(
                                                    v =>
                                                      v.key !==
                                                      propertySearchFilter.key
                                                  )
                                                  .concat(
                                                    value.operator ===
                                                      "allOf" ||
                                                      value.operator === "anyOf"
                                                      ? {
                                                          __typename:
                                                            "EntitySearchPropertyFilterNumberArrayOfFilterComputed",
                                                          key: propertySearchFilter.key,
                                                          operatorNumberArrayOf:
                                                            value.operator,
                                                          expr: value.expr,
                                                          missingValue: true,
                                                          valueNumberArrayOfComputed:
                                                            null,
                                                        }
                                                      : assertNever(
                                                          value.operator
                                                        )
                                                  ),
                                            },
                                          }));

                                          handleClose();
                                        },
                                      }}
                                      disabled={false}
                                    >
                                      <LabeledValueWithButton
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValueWithButton>
                                    </ModalOpenButton>
                                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                                  {viewModePropertyExpression === "delete" && (
                                    <IconButton
                                      onClick={() => {
                                        formikProps.setValues(values => ({
                                          ...values,
                                          templateSearchFilterExpressions: {
                                            propertyFilters:
                                              values.templateSearchFilterExpressions.propertyFilters.filter(
                                                v =>
                                                  v.key !==
                                                  propertySearchFilter.key
                                              ),
                                          },
                                        }));
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Grid>
                            );
                          }
                          case "EntitySearchPropertyFilterTextArrayOfFilterComputed": {
                            return (
                              <Grid key={propertySearchFilter.key} size={1}>
                                <Stack
                                  direction={"row"}
                                  spacing={0.5}
                                  alignItems={"center"}
                                  justifyContent={"space-between"}
                                >
                                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                                    capability="EXPRESSIONS"
                                    permissionByType={{
                                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                                      REQUIREMENT:
                                        "MANAGE_REQUIREMENTS_EXPRESSIONS",
                                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                                    }}
                                    document={document}
                                    otherwise={
                                      <LabeledValue
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValue>
                                    }
                                  >
                                    <ModalOpenButton
                                      Modal={
                                        ProductSearchFilterExpressionModal<EntitySearchArrayOfFilterOperator>
                                      }
                                      modalProps={{
                                        projectId: null,
                                        docId: docId,
                                        itemId: itemId,
                                        filterLabel:
                                          getEnhancedTemplateSearchFilterPropLabel(
                                            propertySearchFilter
                                          ),
                                        operator:
                                          propertySearchFilter.operatorTextArrayOf,
                                        allowedSearchFilterOperators:
                                          ArrayFilterOperatorOptions,
                                        expression: propertySearchFilter.expr,
                                        handleComplete: (
                                          value,
                                          handleClose
                                        ) => {
                                          formikProps.setValues(values => ({
                                            ...values,
                                            templateSearchFilterExpressions: {
                                              propertyFilters:
                                                values.templateSearchFilterExpressions.propertyFilters
                                                  .filter(
                                                    v =>
                                                      v.key !==
                                                      propertySearchFilter.key
                                                  )
                                                  .concat(
                                                    value.operator ===
                                                      "allOf" ||
                                                      value.operator === "anyOf"
                                                      ? {
                                                          __typename:
                                                            "EntitySearchPropertyFilterTextArrayOfFilterComputed",
                                                          key: propertySearchFilter.key,
                                                          operatorTextArrayOf:
                                                            value.operator,
                                                          expr: value.expr,
                                                          missingValue: true,
                                                          valueTextArrayOfComputed:
                                                            null,
                                                        }
                                                      : assertNever(
                                                          value.operator
                                                        )
                                                  ),
                                            },
                                          }));

                                          handleClose();
                                        },
                                      }}
                                      disabled={false}
                                    >
                                      <LabeledValueWithButton
                                        label={getEnhancedTemplateSearchFilterPropLabel(
                                          propertySearchFilter
                                        )}
                                        labelIcon={FunctionsIcon}
                                      >
                                        {getValueWithOperator(
                                          propertySearchFilter
                                        )}
                                      </LabeledValueWithButton>
                                    </ModalOpenButton>
                                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                                  {viewModePropertyExpression === "delete" && (
                                    <IconButton
                                      onClick={() => {
                                        formikProps.setValues(values => ({
                                          ...values,
                                          templateSearchFilterExpressions: {
                                            propertyFilters:
                                              values.templateSearchFilterExpressions.propertyFilters.filter(
                                                v =>
                                                  v.key !==
                                                  propertySearchFilter.key
                                              ),
                                          },
                                        }));
                                      }}
                                      color="primary"
                                      size="small"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Grid>
                            );
                          }
                          default:
                            assertNever(propertySearchFilter);
                        }
                      }
                    )}
                  </Grid>
                </CollapseSection>
                <CollapseSection
                  title={t("Sorting", { ns: "TemplateTypes" })}
                  ActionButtons={
                    <>
                      <MenuButton
                        Icon={<AddIcon />}
                        buttonProps={{
                          disabled: unusedSortingProperties.length === 0,
                        }}
                      >
                        {unusedSortingProperties.map(prop => {
                          return (
                            <MenuItem
                              onClick={() => {
                                formikProps.setValues(values => ({
                                  ...values,
                                  templateSearchSortingDefinitions:
                                    values.templateSearchSortingDefinitions.concat(
                                      prop.__typename === "Props2Number"
                                        ? {
                                            __typename:
                                              "EntitySearchSortingPropertyValueSorting",
                                            key: prop.key,
                                            kind: "number",
                                            direction: "asc",
                                          }
                                        : assertNever(prop.__typename)
                                    ),
                                }));
                              }}
                            >
                              {prop.key}
                            </MenuItem>
                          );
                        })}
                      </MenuButton>
                      <MenuButton>
                        <ViewModeMenuItem
                          allowedModes={[null, "delete"]}
                          viewMode={viewModeSearchSortings}
                          onViewModeChange={setViewModeSearchSortings}
                        />
                      </MenuButton>
                    </>
                  }
                >
                  <Grid
                    container
                    columns={2}
                    rowSpacing={1}
                    columnSpacing={2}
                    padding={1}
                  >
                    {searchSortingDefinitions.map((searchSorting, index) => {
                      switch (searchSorting.__typename) {
                        case "EntitySearchSortingPropertyValueSorting": {
                          return (
                            <Grid key={searchSorting.key} size={1}>
                              <Stack
                                direction={"row"}
                                spacing={0.5}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                              >
                                <Select
                                  key={`${searchSorting.key}-input`}
                                  label={searchSorting.key}
                                  options={[
                                    {
                                      label: "asc",
                                      value: "asc" as const,
                                    },
                                    {
                                      label: "desc",
                                      value: "desc" as const,
                                    },
                                  ]}
                                  value={searchSorting.direction}
                                  onChange={value => {
                                    formikProps.setValues(values => ({
                                      ...values,
                                      templateSearchSortingDefinitions:
                                        values.templateSearchSortingDefinitions.map(
                                          d =>
                                            d.__typename ===
                                              searchSorting.__typename &&
                                            d.key === searchSorting.key
                                              ? { ...d, direction: value }
                                              : d
                                        ),
                                    }));
                                  }}
                                />
                                {viewModeSearchSortings === "delete" && (
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                      formikProps.setValues(values => ({
                                        ...values,
                                        templateSearchSortingDefinitions:
                                          values.templateSearchSortingDefinitions.filter(
                                            d =>
                                              !(
                                                d.__typename ===
                                                  searchSorting.__typename &&
                                                d.key === searchSorting.key
                                              )
                                          ),
                                      }));
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Stack>
                            </Grid>
                          );
                        }
                        default:
                          assertNever(searchSorting.__typename);
                      }
                    })}
                  </Grid>
                </CollapseSection>
              </Stack>
            </Form>
            <ConfirmProcess ref={confirmProcessRef} />
          </Modal>
        );
      }}
    </Formik>
  );
};
